import React, { useMemo, useState, useEffect } from 'react';
import { Popover, Box, Typography, Button, IconButton, CircularProgress } from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';
import type { Notification } from '../types/notification';

/** Chaavi panel tokens (from chaavi_notification_panel.html) */
export const NOTIF_PANEL = {
  panelBg: '#ffffff',
  border: '#d6cfc0',
  headerRule: '#ede8dc',
  text: '#3a2e1e',
  textMuted: '#7a6a55',
  time: '#a89880',
  accent: '#c0873a',
  accentHoverBg: '#fdf3e3',
  unreadBg: '#fffbf4',
  rowHover: '#fdf9f3',
  dot: '#c0873a',
  iconMsg: '#fdf0e0',
  iconOrder: '#e8f4ed',
  iconPromo: '#f0eaff',
  iconAlert: '#fff0e8',
  tagMsgText: '#b07030',
  tagOrderText: '#2e7d4f',
  tagPromoText: '#6b4fbb',
  tagAlertText: '#c05a1a',
} as const;

export type NotifFilterTab = 'all' | 'messages' | 'orders' | 'promos';

function getCategory(n: Notification): NotifFilterTab {
  switch (n.notificationType) {
    case 'STOCK_AVAILABLE':
      return 'promos';
    case 'ORDER_CANCELLED':
    case 'ORDER_CONFIRMED':
    case 'ORDER_PREPARING':
    case 'ORDER_READY':
    case 'ORDER_SHIPPED':
    case 'ORDER_DELIVERED':
      return 'orders';
    default:
      return 'orders';
  }
}

/** Alerts / urgent (cancelled) — still "orders" in filter; visual alert style */
function isAlertStyle(n: Notification): boolean {
  return n.notificationType === 'ORDER_CANCELLED';
}

function categoryLabel(cat: NotifFilterTab): string {
  switch (cat) {
    case 'messages':
      return 'Message';
    case 'orders':
      return 'Order';
    case 'promos':
      return 'Promo';
    default:
      return 'Order';
  }
}

function categoryEmoji(cat: NotifFilterTab, alert: boolean): string {
  if (alert) return '⚠️';
  switch (cat) {
    case 'messages':
      return '💬';
    case 'orders':
      return '🎊';
    case 'promos':
      return '✨';
    default:
      return '🔔';
  }
}

function iconWrapBg(cat: NotifFilterTab, alert: boolean): string {
  if (alert) return NOTIF_PANEL.iconAlert;
  switch (cat) {
    case 'messages':
      return NOTIF_PANEL.iconMsg;
    case 'orders':
      return NOTIF_PANEL.iconOrder;
    case 'promos':
      return NOTIF_PANEL.iconPromo;
    default:
      return NOTIF_PANEL.iconOrder;
  }
}

function tagStyles(cat: NotifFilterTab, alert: boolean): { bg: string; color: string } {
  if (alert) return { bg: NOTIF_PANEL.iconAlert, color: NOTIF_PANEL.tagAlertText };
  switch (cat) {
    case 'messages':
      return { bg: NOTIF_PANEL.iconMsg, color: NOTIF_PANEL.tagMsgText };
    case 'orders':
      return { bg: NOTIF_PANEL.iconOrder, color: NOTIF_PANEL.tagOrderText };
    case 'promos':
      return { bg: NOTIF_PANEL.iconPromo, color: NOTIF_PANEL.tagPromoText };
    default:
      return { bg: NOTIF_PANEL.iconOrder, color: NOTIF_PANEL.tagOrderText };
  }
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} mins ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs} hr${diffHrs === 1 ? '' : 's'} ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function titleFor(n: Notification, cat: NotifFilterTab, alert: boolean): string {
  if (alert) {
    if (n.notificationType === 'ORDER_CANCELLED') return 'Order cancelled';
    return 'Important update';
  }
  switch (n.notificationType) {
    case 'ORDER_CONFIRMED':
      return 'Booking confirmed';
    case 'ORDER_PREPARING':
      return 'Order preparing';
    case 'ORDER_READY':
      return 'Order ready';
    case 'ORDER_SHIPPED':
      return 'Out for delivery';
    case 'ORDER_DELIVERED':
      return 'Delivered';
    case 'STOCK_AVAILABLE':
      return 'Stock available';
    default:
      break;
  }
  if (n.businessName) return n.businessName;
  return 'Notification';
}

function descriptionFor(n: Notification): string {
  let d = n.message?.trim() || '';
  if (n.notificationType === 'STOCK_AVAILABLE' && n.deliveryDate) {
    const avail = new Date(n.deliveryDate).toLocaleDateString();
    d += (d ? ' ' : '') + `Available ${avail}.`;
  }
  if (!d && n.businessName) {
    d = `From ${n.businessName}`;
  }
  return d || 'Tap to view details.';
}

export interface ChaaviNotificationPanelProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  notifications: Notification[];
  loading?: boolean;
  onItemClick: (n: Notification) => void;
  onMarkAllRead: () => void | Promise<void>;
  onDismissItem: (n: Notification) => void | Promise<void>;
  onViewAll: () => void;
  viewAllLabel?: string;
}

const TABS: { id: NotifFilterTab; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'messages', label: 'Messages' },
  { id: 'orders', label: 'Orders' },
  { id: 'promos', label: 'Promos' },
];

const ChaaviNotificationPanel: React.FC<ChaaviNotificationPanelProps> = ({
  anchorEl,
  open,
  onClose,
  notifications,
  loading = false,
  onItemClick,
  onMarkAllRead,
  onDismissItem,
  onViewAll,
  viewAllLabel = 'View all activity',
}) => {
  const [filter, setFilter] = useState<NotifFilterTab>('all');

  useEffect(() => {
    if (open) setFilter('all');
  }, [open]);

  const sorted = useMemo(
    () => [...notifications].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [notifications]
  );

  const filtered = useMemo(() => {
    if (filter === 'all') return sorted;
    return sorted.filter(n => getCategory(n) === filter);
  }, [sorted, filter]);

  const showEmpty = !loading && filtered.length === 0;

  return (
    <Popover
      open={open && Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        elevation: 0,
        sx: {
          width: 380,
          maxWidth: 'calc(100vw - 24px)',
          mt: 1,
          borderRadius: '12px',
          border: `0.5px solid ${NOTIF_PANEL.border}`,
          boxShadow: '0 8px 32px rgba(80, 60, 20, 0.10)',
          overflow: 'hidden',
          bgcolor: NOTIF_PANEL.panelBg,
        },
      }}
      disableRestoreFocus
    >
      <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2.5,
              pt: 2,
              pb: 1.5,
              borderBottom: `0.5px solid ${NOTIF_PANEL.headerRule}`,
            }}
          >
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 500,
                color: NOTIF_PANEL.text,
                fontFamily: '"Playfair Display", Georgia, serif',
              }}
            >
              Notifications
            </Typography>
            <Button
              size="small"
              onClick={() => onMarkAllRead()}
              disabled={notifications.length === 0 || !notifications.some(n => !n.isRead)}
              sx={{
                fontSize: '0.75rem',
                color: NOTIF_PANEL.accent,
                textTransform: 'none',
                minWidth: 0,
                px: 1,
                py: 0.5,
                fontFamily: '"Jost", "Roboto", sans-serif',
                '&:hover': { bgcolor: NOTIF_PANEL.accentHoverBg },
                '&.Mui-disabled': { color: NOTIF_PANEL.time },
              }}
            >
              Mark all read
            </Button>
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              px: 2.5,
              pt: 1.5,
              pb: 1,
              borderBottom: `0.5px solid ${NOTIF_PANEL.headerRule}`,
              flexWrap: 'wrap',
            }}
          >
            {TABS.map(tab => (
              <Button
                key={tab.id}
                size="small"
                onClick={() => setFilter(tab.id)}
                sx={{
                  fontSize: '0.75rem',
                  py: 0.4,
                  px: 1.5,
                  borderRadius: '20px',
                  textTransform: 'none',
                  minWidth: 0,
                  fontFamily: '"Jost", "Roboto", sans-serif',
                  border: `0.5px solid ${NOTIF_PANEL.border}`,
                  ...(filter === tab.id
                    ? {
                        bgcolor: NOTIF_PANEL.accent,
                        color: '#fff',
                        borderColor: NOTIF_PANEL.accent,
                        '&:hover': { bgcolor: NOTIF_PANEL.accent },
                      }
                    : {
                        bgcolor: 'transparent',
                        color: NOTIF_PANEL.textMuted,
                        '&:hover': { bgcolor: '#f5f0e8' },
                      }),
                }}
              >
                {tab.label}
              </Button>
            ))}
          </Box>

          <Box
            sx={{
              maxHeight: 360,
              overflowY: 'auto',
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                background: NOTIF_PANEL.border,
                borderRadius: 2,
              },
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                <CircularProgress size={28} sx={{ color: NOTIF_PANEL.accent }} />
              </Box>
            ) : showEmpty ? (
              <Box sx={{ py: 6, px: 2.5, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '2rem', mb: 1.5 }}>🔔</Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: NOTIF_PANEL.textMuted,
                    fontFamily: '"Jost", sans-serif',
                    fontWeight: 500,
                  }}
                >
                  All caught up!
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: NOTIF_PANEL.time,
                    mt: 0.5,
                    fontFamily: '"Jost", sans-serif',
                  }}
                >
                  No notifications in this category.
                </Typography>
              </Box>
            ) : (
              filtered.map(n => {
                const cat = getCategory(n);
                const alert = isAlertStyle(n);
                const emoji = categoryEmoji(cat, alert);
                const ibg = iconWrapBg(cat, alert);
                const tag = tagStyles(cat, alert);
                const tagLabel = alert ? 'Alert' : categoryLabel(cat);

                return (
                  <Box
                    key={n.notificationId}
                    onClick={() => onItemClick(n)}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      px: 2.5,
                      py: 1.75,
                      borderBottom: `0.5px solid #f0ebe0`,
                      cursor: 'pointer',
                      position: 'relative',
                      transition: 'background 0.12s',
                      bgcolor: !n.isRead ? NOTIF_PANEL.unreadBg : 'transparent',
                      '&:hover': { bgcolor: !n.isRead ? NOTIF_PANEL.unreadBg : NOTIF_PANEL.rowHover },
                      '&::before':
                        !n.isRead
                          ? {
                              content: '""',
                              position: 'absolute',
                              left: 8,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              width: 5,
                              height: 5,
                              borderRadius: '50%',
                              bgcolor: NOTIF_PANEL.dot,
                            }
                          : undefined,
                    }}
                  >
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: '50%',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1rem',
                        bgcolor: ibg,
                        ml: !n.isRead ? 0.5 : 0,
                      }}
                    >
                      {emoji}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: '0.8125rem',
                          fontWeight: 500,
                          color: NOTIF_PANEL.text,
                          fontFamily: '"Jost", sans-serif',
                          lineHeight: 1.4,
                        }}
                      >
                        {titleFor(n, cat, alert)}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: NOTIF_PANEL.textMuted,
                          mt: 0.25,
                          lineHeight: 1.5,
                          fontFamily: '"Jost", sans-serif',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {descriptionFor(n)}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.65, flexWrap: 'wrap' }}>
                        <Typography
                          sx={{
                            fontSize: '0.6875rem',
                            color: NOTIF_PANEL.time,
                            fontFamily: '"Jost", sans-serif',
                          }}
                        >
                          {formatRelativeTime(n.createdAt)}
                        </Typography>
                        <Typography
                          component="span"
                          sx={{
                            fontSize: '0.625rem',
                            px: 0.85,
                            py: 0.125,
                            borderRadius: '10px',
                            fontFamily: '"Jost", sans-serif',
                            fontWeight: 500,
                            bgcolor: tag.bg,
                            color: tag.color,
                          }}
                        >
                          {tagLabel}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', pt: 0.25 }}>
                      <IconButton
                        size="small"
                        aria-label="Dismiss"
                        onClick={e => {
                          e.stopPropagation();
                          onDismissItem(n);
                        }}
                        sx={{
                          width: 28,
                          height: 28,
                          color: '#c0b8a8',
                          '&:hover': { bgcolor: '#f0ebe0', color: NOTIF_PANEL.textMuted },
                        }}
                      >
                        <CloseRounded sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>

          <Box
            sx={{
              py: 1.5,
              px: 2.5,
              borderTop: `0.5px solid ${NOTIF_PANEL.headerRule}`,
              textAlign: 'center',
            }}
          >
            <Button
              onClick={onViewAll}
              sx={{
                fontSize: '0.8125rem',
                color: NOTIF_PANEL.accent,
                textTransform: 'none',
                fontFamily: '"Jost", sans-serif',
                p: 0,
                minWidth: 0,
                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
              }}
            >
              {viewAllLabel}
            </Button>
          </Box>
        </Box>
    </Popover>
  );
};

export default ChaaviNotificationPanel;
